import React from 'react';
import xss from 'xss';
import {
  ERROR_BURST_LIMIT,
  ERROR_KINDS,
  type ErrorKind,
  ERROR_SUSTAINED_PER_SECOND,
  isNoise,
  MESSAGE_DEDUPE_WINDOW_MS,
} from './constants';
import { initLogShipper } from './log-shipper';
import { translateStackTrace } from './source-map-service';

declare global {
  interface Window {
    __VIBE_ERROR_DEBUG__?: boolean;
  }
}

type ReactErrorInfo = { componentStack?: string | null };

type FormatOptions = {
  ownerStack?: string | null;
  componentStack?: string | null;
  errorStack?: string | null;
  cause?: unknown;
};

const recentMessages = new Set<string>();
let initialized = false;

export function init(): void {
  if (initialized) return;
  initialized = true;

  interceptConsoleError();
  interceptWindowErrors();
  interceptUnhandledRejections();
  initLogShipper();
}

function interceptConsoleError(): void {
  const originalConsoleError = console.error;

  console.error = (...args: unknown[]) => {
    originalConsoleError.apply(console, args);

    // React's DEV warnings (key, hooks-order, nested HTML, prop-on-DOM, NaN, ...)
    // are emitted via console.error from inside react-dom's printWarning, which
    // always calls console.error with a format string containing %s/%o
    // placeholders + substitution args, and never an Error instance. Real
    // render-time errors are *thrown* and routed to onUncaughtError /
    // onCaughtError / onRecoverableError instead. User code calling
    // console.error('Failed to fetch:', err) doesn't fit the React shape.
    if (isReactDevWarning(args)) return;

    const firstError = args.find((arg): arg is Error => arg instanceof Error);
    const content = args.map((arg) => (arg instanceof Error ? errorToString(arg) : stringifyValue(arg))).join(' ');

    formatMessage(content, {
      errorStack: firstError?.stack,
      cause: errorDetails(firstError),
    }).then((msg) => sendToParent(msg, ERROR_KINDS.CONSOLE, firstError?.stack));
  };
}

function isReactDevWarning(args: unknown[]): boolean {
  // React DEV's printWarning shape: first arg is a string with %s/%o/%d/%i/%f
  // placeholders, followed by substitution values. No Error instance is ever
  // attached — warnings are pure messages.
  if (typeof args[0] !== 'string') return false;
  if (!/%[sodif]/.test(args[0])) return false;
  if (args.some((arg) => arg instanceof Error)) return false;
  return true;
}

function interceptWindowErrors(): void {
  const capturedErrors = new WeakSet<Error>();

  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      const { message, filename, lineno, colno, error } = event;

      if (error instanceof Error) {
        if (capturedErrors.has(error)) return;
        capturedErrors.add(error);

        formatError(error).then((msg) => sendToParent(msg, ERROR_KINDS.UNCAUGHT, error.stack));
      } else if (message) {
        const location = filename ? ` at ${filename}:${lineno}:${colno}` : '';
        formatMessage(`${message}${location}`).then((msg) => sendToParent(msg, ERROR_KINDS.UNCAUGHT));
      }

      return false;
    },
    true,
  );
}

function interceptUnhandledRejections(): void {
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;

    if (reason instanceof Error) {
      formatError(reason).then((msg) => sendToParent(msg, ERROR_KINDS.UNHANDLED_REJECTION, reason.stack));
    } else {
      formatMessage(stringifyValue(reason)).then((msg) => sendToParent(msg, ERROR_KINDS.UNHANDLED_REJECTION));
    }
  });
}

function createReactErrorHandler(kind: ErrorKind) {
  return (error: unknown, errorInfo?: ReactErrorInfo) => {
    const ownerStack = captureOwnerStack();
    formatError(error, {
      ownerStack,
      componentStack: errorInfo?.componentStack,
    }).then((msg) => sendToParent(msg, kind, error instanceof Error ? error.stack : undefined));
  };
}

export const onCaughtError = createReactErrorHandler(ERROR_KINDS.REACT_CAUGHT);
export const onUncaughtError = createReactErrorHandler(ERROR_KINDS.REACT_UNCAUGHT);
export const onRecoverableError = createReactErrorHandler(ERROR_KINDS.REACT_RECOVERABLE);

// --- Formatting ---

// The diagnostic payload attached to an error. Besides the standard `cause`,
// monday API clients attach the GraphQL errors array (field-level detail the
// auto-fix loop needs) as `error.response.errors` (SeamlessApiClientError) or
// `error.data.errors` (monday-sdk-js) — without it the reported message is just
// the opaque "Graphql validation errors".
function errorDetails(error: unknown): unknown {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as { cause?: unknown; response?: { errors?: unknown }; data?: { errors?: unknown } };
  return err.cause ?? err.response?.errors ?? err.data?.errors;
}

function formatError(error: unknown, { ownerStack, componentStack }: FormatOptions = {}): Promise<string> {
  return formatMessage(errorToString(error), {
    ownerStack,
    componentStack,
    errorStack: error instanceof Error ? error.stack : undefined,
    cause: errorDetails(error),
  });
}

async function formatMessage(content: string, { ownerStack, componentStack, errorStack, cause }: FormatOptions = {}): Promise<string> {
  const hasOnlyErrorStack = errorStack && !ownerStack && !componentStack && !cause;

  const parts: string[] = [content];

  if (hasOnlyErrorStack) {
    parts.push(await translateStackTrace(errorStack));
  } else {
    if (ownerStack) {
      parts.push(`Owner Stack:${await translateStackTrace(ownerStack)}`);
    }
    if (componentStack) {
      parts.push(`Component Stack:${await translateStackTrace(componentStack)}`);
    }
    if (errorStack) {
      parts.push(`Error Stack:\n${await translateStackTrace(errorStack)}`);
    }
    if (cause) {
      parts.push(`Original Cause: ${formatCause(cause)}`);
    }
  }

  return sanitize(parts.join('\n\n'));
}

function errorToString(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return stringifyValue(error);
}

const CAUSE_FIELD_MAX_LEN = 4000;

function formatCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message || String(cause);
  }
  if (typeof cause === 'string') {
    return cause;
  }
  if (cause && typeof cause === 'object') {
    try {
      return JSON.stringify(cause, (_key, val) => {
        if (typeof val === 'string' && val.length > CAUSE_FIELD_MAX_LEN) {
          return val.slice(0, CAUSE_FIELD_MAX_LEN) + '…[truncated]';
        }
        return val;
      });
    } catch {
      return String(cause);
    }
  }
  return String(cause);
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function sanitize(text: string): string {
  return xss(String(text), {
    whiteList: {},
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['script'],
  });
}

// --- Infrastructure ---

function captureOwnerStack(): string | null {
  const captureFn = (React as unknown as { captureOwnerStack?: () => string | null }).captureOwnerStack;
  return typeof captureFn === 'function' ? captureFn() : null;
}

// Token bucket (same shape as log-shipper.ts) so a crash/retry loop can't emit
// tens of thousands of error events.
let availableErrors = ERROR_BURST_LIMIT;
let lastErrorRefillAt = Date.now();
let droppedErrors = 0;

function allowError(): boolean {
  const now = Date.now();
  availableErrors = Math.min(
    ERROR_BURST_LIMIT,
    availableErrors + ((now - lastErrorRefillAt) / 1000) * ERROR_SUSTAINED_PER_SECOND,
  );
  lastErrorRefillAt = now;
  if (availableErrors < 1) {
    droppedErrors += 1;
    return false;
  }
  availableErrors -= 1;
  return true;
}

function sendToParent(message: string, kind: ErrorKind, stack?: string): void {
  try {
    if (isNoise(message)) return;

    const messageKey = `${kind}:${message.substring(0, 100)}`;
    if (recentMessages.has(messageKey)) return;

    recentMessages.add(messageKey);
    setTimeout(() => recentMessages.delete(messageKey), MESSAGE_DEDUPE_WINDOW_MS);

    logDebug(kind, message);

    if (!allowError()) return;

    if (window.top && window.top !== window) {
      const dropNote = droppedErrors > 0 ? `[reporter dropped ${droppedErrors} earlier error(s) due to rate cap]\n` : '';
      droppedErrors = 0;
      window.top.postMessage(
        {
          type: 'VIBE_CONSOLE_ERROR',
          kind,
          message: dropNote + message,
          // Raw (untranslated) stack, sent separately so analytics can group on a
          // clean message while keeping the stack for diagnosis.
          stack: typeof stack === 'string' ? stack.slice(0, 8000) : undefined,
        },
        '*',
      );
    }
  } catch {
    // Silent fail to avoid infinite loops
  }
}

function logDebug(kind: ErrorKind, message: string): void {
  if (!window.__VIBE_ERROR_DEBUG__) return;

  const headerStyle = 'background: #ff6b6b; color: white; padding: 2px 8px; border-radius: 3px; font-weight: bold;';
  const kindStyle = 'background: #1a535c; color: white; padding: 2px 6px; border-radius: 3px; margin-left: 4px;';

  console.log('%c ERROR CAPTURED %c %s ', headerStyle, kindStyle, kind);
  console.log(message);
}
